import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  adminManageService,
  type CreateManagedUserPayload,
  type DeleteManagedUserOptions,
  type ManagedTrialDto,
  type ManagedUserDto,
  type UpdateManagedUserPayload,
} from "@/services/adminManageService";

export const adminManageUsersKeys = {
  all: ["admin-manage-users"] as const,
  lists: () => [...adminManageUsersKeys.all, "list"] as const,
  list: ({ trial }: { trial?: string } = {}) =>
    [...adminManageUsersKeys.lists(), { trial: trial ?? null }] as const,
  details: () => [...adminManageUsersKeys.all, "detail"] as const,
  detail: (userId: string) =>
    [...adminManageUsersKeys.details(), userId] as const,
  trials: () => [...adminManageUsersKeys.all, "trials"] as const,
};

export function useAdminManagedUsers({ trial }: { trial?: string } = {}) {
  return useQuery<ManagedUserDto[]>({
    queryKey: adminManageUsersKeys.list({ trial }),
    queryFn: () => adminManageService.listManagedUsers({ trial }),
    staleTime: 60 * 1000,
  });
}

export function useAdminManagedTrials() {
  return useQuery<ManagedTrialDto[]>({
    queryKey: adminManageUsersKeys.trials(),
    queryFn: () => adminManageService.listManagedTrials(),
    staleTime: 60 * 1000,
  });
}

export function useManagedUser(userId?: string) {
  return useQuery<ManagedUserDto>({
    queryKey: adminManageUsersKeys.detail(userId ?? ""),
    queryFn: () => adminManageService.getManagedUser(userId as string),
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
  });
}

async function invalidateAdminManageUsers(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  await queryClient.invalidateQueries({ queryKey: adminManageUsersKeys.all });
}

export function useCreateManagedUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateManagedUserPayload) =>
      adminManageService.createManagedUser(payload),
    onSuccess: async (user) => {
      await invalidateAdminManageUsers(queryClient);
      await queryClient.invalidateQueries({
        queryKey: adminManageUsersKeys.detail(user.id),
      });
    },
  });
}

export function useUpdateManagedUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: string;
      payload: UpdateManagedUserPayload;
    }) => adminManageService.updateManagedUser(userId, payload),
    onSuccess: async (user) => {
      await invalidateAdminManageUsers(queryClient);
      await queryClient.invalidateQueries({
        queryKey: adminManageUsersKeys.detail(user.id),
      });
    },
  });
}

export function useDeleteManagedUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      options,
    }: {
      userId: string;
      options?: DeleteManagedUserOptions;
    }) => adminManageService.deleteManagedUser(userId, options),
    onSuccess: async () => {
      await invalidateAdminManageUsers(queryClient);
    },
  });
}
